import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { personaColumn, searchFilter } from '../../../helpers/detailsListHelper'
import DetailsListAbstract, {
    ICoreColumn,
    ICoreDetailsListFilter,
    ICoreDetailsListSettings,
    IDynamicContextualMenuItem,
} from '../../DetailsList/DetailsListAbstract'
import { Link, useHistory } from 'react-router-dom'
import { History } from 'history'
import CopyFileDialog from '../../Dialog/CopyFile/CopyFileDialog'
import { FileStatus, IOverviewItemDto } from '../../../config/fileConfig'
import { DataPromise } from '../../../context/AxiosContext'
import FormattedDateTime from '../../Date/FormattedDateTime'
import useRoles, { Role } from '../../../hooks/useRoles'
import { AttributeEntityDto } from '../../../hooks/useAttributes'
import { FileSortDto, SortDirection } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'
import { useTranslation } from 'react-i18next'

export enum UserSearchMode {
    AND = 'AND',
    OR = 'OR',
}

export interface IFileListSettings extends ICoreDetailsListSettings {
    filter: FileListFilter
}

export type FileListFilter = AttributeEntityDto & {
    statuses?: FileStatus[]
    ownerUsers?: string[]
    userSearchMode?: UserSearchMode
    executiveUsers?: string[]
}

type Props = {
    files: DataPromise<IOverviewItemDto[], IFileListSettings>
    filters?: ICoreDetailsListFilter<IOverviewItemDto, IFileListSettings>[]
    sorting?: FileSortDto
}

type CurrentFile = Pick<IOverviewItemDto, 'id' | 'name'>

const getColumns = (props: Props, translate: (key: string) => string): ICoreColumn[] => [
    personaColumn({
        key: 'owner',
        name: 'Verantwoordelijke',
    }),
    {
        key: 'name',
        name: translate('base.table.header.name'),
        fieldName: 'name',
        minWidth: 240,
        isResizable: true,
        isRowHeader: true,
        isSortable: true,
        isSorted: props.sorting?.field === 'name',
        data: (item: IOverviewItemDto) => item.name,
        onRender: (item: IOverviewItemDto) => (
            <Link
                key={item.id}
                to={`/file/${item.id}`}
                className={item.status === FileStatus.Open ? 'item-open' : 'item-closed'}
            >
                {item.name}
            </Link>
        ),
    },
    {
        key: 'reference',
        name: 'Dossiernummer',
        fieldName: 'reference',
        minWidth: 90,
        maxWidth: 100,
        isResizable: true,
        isIconOnly: true,
        iconName: 'Album',
        isSortedDescending: true,
        isSortable: true,
        isSorted: props.sorting?.field === 'reference',
        data: (item: IOverviewItemDto) => item.reference,
    },
    {
        key: 'lastAccess',
        name: 'Laatst bekeken',
        fieldName: 'lastAccess',
        minWidth: 70,
        isResizable: true,
        isRowHeader: false,
        isIconOnly: true,
        iconName: 'View',
        isSortedDescending: true,
        isSortable: true,
        isSorted: props.sorting?.field === 'lastAccess',
        data: (item: IOverviewItemDto) => item.lastAccess,
        onRender: (item: IOverviewItemDto) => <FormattedDateTime date={item.lastAccess}></FormattedDateTime>,
    },
]

const getMenuItems = (
    history: History,
    translate: (key: string) => string,
    setCurrentFile: (currentFile: CurrentFile) => void,
): IDynamicContextualMenuItem[] => [
    {
        key: 'edit',
        iconProps: { iconName: 'Edit' },
        text: translate('action.edit'),
        click: (item: IOverviewItemDto) => history.push(`/file/${item.id}/edit`),
    },
    {
        key: 'copy',
        iconProps: { iconName: 'Copy' },
        text: translate('action.copy'),
        access: () => false, //Temporarily disable copying of files
        click: setCurrentFile,
    },
]

const getFilters = (columns: ICoreColumn[]): ICoreDetailsListFilter<IOverviewItemDto, IFileListSettings>[] => [
    searchFilter(columns),
]

export default function FileList(props: Props): ReactElement {
    const settings = useSettings()
    const roles = useRoles()
    const history = useHistory()
    const { t: translate } = useTranslation()

    const [columns, setColumns] = useState<ICoreColumn[]>([])
    const [filters, setFilters] = useState<ICoreDetailsListFilter<IOverviewItemDto, IFileListSettings>[]>([])
    const [currentFile, setCurrentFile] = useState<CurrentFile>()
    const [menuItems, setMenuItems] = useState<IDynamicContextualMenuItem[]>([])
    const [sorting, setSorting] = useState<FileSortDto>()

    useEffect(() => {
        setSorting(props.sorting ?? settings?.fileSort ?? { field: 'reference', direction: SortDirection.Descending })
    }, [settings])

    useEffect(() => {
        setColumns(getColumns({ ...props, sorting: sorting }, translate))
    }, [sorting])

    useEffect(() => {
        setFilters(getFilters(columns))
    }, [columns])

    useEffect(() => {
        if (roles.none([Role.FileLimited])) {
            setMenuItems(getMenuItems(history, translate, setCurrentFile))
        }
    }, [currentFile])

    return (
        <>
            <DetailsListAbstract
                columns={columns}
                items={props.files.data}
                filters={filters}
                settings={props.files.settings}
                isSortable
                onSortClicked={column =>
                    setSorting({
                        field: column.fieldName,
                        direction: column.isSortedDescending ? SortDirection.Descending : SortDirection.Ascending,
                    })
                }
                contextualMenuItems={[menuItems]}
                getContextualMenuTitle={(item: IOverviewItemDto) => `${item.reference} - ${item.name}`}
                emptyDescription={translate('base.table.empty')}
            />
            {currentFile && (
                <CopyFileDialog
                    hidden={false}
                    key={currentFile.id}
                    id={currentFile.id}
                    name={currentFile.name}
                    onDismiss={() => setCurrentFile(undefined)}
                />
            )}
        </>
    )
}
